import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CoverageLevelV2, TeamAPIV2 } from '../types';

/**
 * The Fulfilment card in the left rail (team_proposal_spec §4, §5).
 *
 * <p>Answers one question: does the seated team satisfy what the tender asks of
 * the team as a whole — deterministic aggregation over findings, satisfied by at
 * least one member. The Company tab is the firm-level equivalent, where the
 * subject is the bidding firm and no CV can answer.
 *
 * <p><strong>Seats are not repeated here.</strong> The roles rail above already
 * shows every chair the tender asks for, who sits in it, and why an empty one is
 * empty. Restating that as a second list of gaps gave the recruiter the same
 * fact twice in two shapes and buried the thing this card alone can say: which
 * team-wide requirements the selection answers and which it does not.
 *
 * <p>It reports and never decides. §5 keeps the go / no-go call with the
 * recruiter, so a tender that admits no partial response is stated as such and
 * nothing more.
 */

interface Props {
  team: TeamAPIV2;
}

const LEVEL: Record<CoverageLevelV2, { mark: string; pill: string }> = {
  COVERED: { mark: '✓', pill: 'bg-green-50 text-green-800 border-green-200' },
  THIN: { mark: '~', pill: 'bg-amber-50 text-amber-800 border-amber-200' },
  UNCOVERED: { mark: '✕', pill: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function FulfilmentRail({ team }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'TEAM' | 'COMPANY'>('TEAM');

  if (team.outcome === 'SINGLE_POSITION') return null;

  const covered = team.coverage.filter((c) => c.level === 'COVERED').length;
  const total = team.coverage.length;
  const allCovered = total > 0 && covered === total;
  const allOrNothing = team.feasibility?.all_or_nothing ?? false;
  const isPool = team.outcome === 'CAPACITY_POOL';

  const nameOf = (id: string) =>
    team.seats.find((s) => s.candidate?.id === id)?.candidate?.name ?? id;

  return (
    <aside className="bg-white rounded-xl border border-gray-200 overflow-hidden self-start">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">
          {t('teamProposal.fulfilment', 'Fulfilment')}
        </h2>
        <span className="text-xs text-gray-400">
          {tab === 'TEAM' && !isPool && total > 0
            ? t('teamProposal.coveredOf', '{{covered}}/{{total}} covered', { covered, total })
            : ''}
        </span>
      </div>

      {!isPool && (
        <div className="flex gap-1 px-3 pt-3">
          <button
            type="button"
            onClick={() => setTab('TEAM')}
            aria-pressed={tab === 'TEAM'}
            className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              tab === 'TEAM'
                ? allCovered
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            {t('teamProposal.tabTeam', 'Team')}
            {total > 0 && (
              <span className="ml-1 text-gray-400">
                {covered}/{total}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab('COMPANY')}
            aria-pressed={tab === 'COMPANY'}
            className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              tab === 'COMPANY'
                ? 'border-brand-200 bg-brand-50 text-brand-700'
                : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            {t('teamProposal.tabCompany', 'Company')}
          </button>
        </div>
      )}

      {isPool ? (
        <PoolStatement team={team} />
      ) : tab === 'COMPANY' ? (
        <CompanyStatement />
      ) : team.coverage.length === 0 ? (
        <p className="px-4 py-4 text-[11px] text-gray-400">
          {t(
            'teamProposal.noTeamCriteria',
            'No team criteria yet — mark one from the criteria rail.',
          )}
        </p>
      ) : (
        <>
          {/* One line on the requirements as a whole, so the state is readable
              without counting pills. */}
          <div
            className={`px-4 py-3 text-xs leading-relaxed border-b border-gray-100 ${
              allCovered ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-900'
            }`}
          >
            {allCovered
              ? t('teamProposal.railAllCovered', 'Every team requirement is covered.')
              : t('teamProposal.railNotCovered', '{{count}} of {{total}} not covered by this team.', {
                  count: total - covered,
                  total,
                })}
          </div>

          {/* One row per team-wide requirement, with who carries it. */}
          {team.coverage.map((c) => (
            <div
              key={c.criterion_id}
              className="px-4 py-2.5 border-b border-gray-100 last:border-b-0 text-xs"
            >
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${LEVEL[c.level].pill}`}
              >
                {LEVEL[c.level].mark} {c.criterion ?? c.criterion_id}
              </span>
              <p
                className={`mt-1 text-[11px] ${
                  c.level === 'COVERED'
                    ? 'text-gray-500'
                    : c.level === 'THIN'
                      ? 'text-amber-700'
                      : 'text-rose-700'
                }`}
              >
                {c.level === 'COVERED'
                  ? t('teamProposal.coveredBy', 'covered by {{who}}', {
                      who: c.covering.map(nameOf).join(', '),
                    })
                  : c.level === 'THIN'
                    ? t('teamProposal.thinlyCoveredBy', 'thinly covered by {{who}} — a single weak cue', {
                        who: c.covering.map(nameOf).join(', '),
                      })
                    : (team.feasibility?.coverage_gaps.find(
                          (g) => g.criterion_id === c.criterion_id,
                        )?.could_cover.length ?? 0) > 0
                      ? t('teamProposal.couldCover', 'not covered by this selection — {{count}} could', {
                          count:
                            team.feasibility?.coverage_gaps.find(
                              (g) => g.criterion_id === c.criterion_id,
                            )?.could_cover.length ?? 0,
                        })
                      : t('teamProposal.gapNobodyCovers', 'no candidate on this tender covers this')}
              </p>
            </div>
          ))}

          {/* §5's go / no-go framing. Kept because it is about the bid as a
              whole and is stated nowhere else on the page — not a restatement
              of any seat. */}
          {allOrNothing && !allCovered && (
            <p className="px-4 py-3 text-[11px] text-rose-800 bg-rose-50 border-t border-rose-100">
              {t(
                'teamProposal.railNoPartial',
                'This tender admits no partial response — the bid cannot go out as it stands.',
              )}
            </p>
          )}
        </>
      )}
    </aside>
  );
}

/** §3.2: the tender asks for capacity. Saying so is the output. */
function PoolStatement({ team }: { team: TeamAPIV2 }) {
  const { t } = useTranslation();
  return (
    <div className="px-4 py-3 text-xs text-amber-900 bg-amber-50">
      <p className="font-semibold">
        {t('teamProposal.capacityTitle', 'This tender asks for capacity, not a team')}
      </p>
      <ul className="mt-2 space-y-1">
        {team.demand.map((line) => (
          <li key={line.position_ordinal} className="flex justify-between gap-2">
            <span className="font-medium">{line.title}</span>
            <span>{line.quantity}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The firm-level readout. No firm-facts store exists yet
 * (company_requirements_spec §5), so the tool states no verdict rather than
 * inventing one — the recruiter confirms by hand.
 */
function CompanyStatement() {
  const { t } = useTranslation();
  return (
    <p className="px-4 py-4 text-[11px] text-gray-500 leading-relaxed">
      {t(
        'teamProposal.companyPending',
        'What this tender requires of the firm. Stafflens holds no firm facts, so it states no verdict — confirm the firm’s standing before pitching.',
      )}
    </p>
  );
}
